import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { format, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

export const formatBirthday = (
  dateUnformatted: string,
  monthFormat: "long" | "short" = "long",
): string => {
  let [month, day] = dateUnformatted.split("/");
  if (!dateUnformatted.includes("/")) {
    [month, day] = dateUnformatted.split("-");
  }

  return Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: monthFormat,
    timeZone: "UTC",
  }).format(new Date(`2020-${month}-${day}`)); // 2020 is a leap year
};

export const formatHireDate = (dateUnformatted: string): string => {
  let [month, year] = dateUnformatted.split("/");
  if (!dateUnformatted.includes("/")) {
    [year, month] = dateUnformatted.split("-");
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${year}-${month}-01`));
};

export const getLocalTime = (timezoneOffset: number): string => {
  try {
    const nowUTC = new Date();
    const targetTime = utcToZonedTime(nowUTC, "UTC");
    const localTime = new Date(targetTime.getTime() + timezoneOffset * 1000);
    return localTime.toLocaleString("en-US", {
      weekday: "long",
      hour: "numeric",
      minute: "numeric",
    });
  } catch (e) {
    return "";
  }
};

const capitalizeWords = (wordsSring: string) => {
  const words = wordsSring.split(" ");
  const formattedWords = words.map((word) => {
    if (word.toLowerCase().startsWith("non-")) return word; // exclude "non-"
    return (
      word.toLowerCase().charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
  });
  return formattedWords.filter((word) => !!word).join(" ");
};

export const formatLocation = (location?: string | null): string => {
  if (!location) {
    return location ?? "";
  }
  if (location.indexOf(" - ") >= 0) {
    return location;
  }
  const words = location.split(",");
  if (!words.length) {
    return location;
  }
  if (words.length == 1) {
    return capitalizeWords(words[0]);
  }
  if (words.length == 2) {
    const formattedWords = capitalizeWords(words[0]);
    const formattedState = words[1].toUpperCase();
    return [formattedWords, formattedState].join(",");
  }
  return location;
};

export const toMetro = (location: string) => {
  const metroSplit = location?.split(" - ");
  const metro = metroSplit?.[metroSplit.length - 1];

  return formatLocation(metro);
};

export const formatLeaveDate = (dateUnformatted: string): string => {
  const parsedDate = parseISO(dateUnformatted);
  return format(parsedDate, "MMM d");
};

export const formatUpcomingPTO = (upcomingPTO: {
  start_date: string | null;
  end_date: string | null;
}): string | undefined => {
  if (!upcomingPTO.start_date || !upcomingPTO.end_date) {
    return undefined;
  }
  const startDate = formatLeaveDate(upcomingPTO.start_date);
  const endDate = formatLeaveDate(upcomingPTO.end_date);
  return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
};

export const isMoreThanToday = (startDate: string): boolean => {
  const parsedDate = new Date(startDate);
  parsedDate.setUTCHours(0, 0, 0, 0);
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return parsedDate.getTime() > today.getTime();
};

export const calculateTenure = (tenure: number): string => {
  if (tenure < 1) {
    return i18n.t("less_month");
  }

  const years = Math.floor(tenure / 12);
  const months = tenure % 12;

  if (years > 0 && months === 1) {
    return i18n.t("years_and_one_month", {
      yearsCount: years,
      monthsCount: months,
    });
  } else if (years > 0 && months > 0) {
    return i18n.t("years_and_months", {
      yearsCount: years,
      monthsCount: months,
    });
  } else if (years > 0 && months === 0) {
    return i18n.t("year", { count: years });
  } else if (years === 0 && months > 0) {
    return i18n.t("month", { count: months });
  }
  return "";
};

export const calculateTenurePercentile = (
  tenure_percentile: number,
): number => {
  // Floor the number to one decimal place
  let floored = Math.floor(tenure_percentile * 10) / 10;
  if (Math.floor(tenure_percentile * 100) % 10 >= 5) {
    floored += 0.1;
  }
  return parseFloat(floored.toFixed(1));
};

export const getUserProfilePageHref = (
  email: string | undefined,
): string | undefined => {
  if (!email?.length) {
    return undefined;
  }
  return RoutePath.USER_PROFILE.replace(":id", email.split("@")[0]);
};

export const getEditUserProfilePageHref = (
  email: string | undefined,
): string | undefined => {
  if (!email?.length) {
    return undefined;
  }
  return RoutePath.EDIT_USER_PROFILE.replace(":id", email.split("@")[0]);
};

export const canUserEditUserProfile = (
  currentUserEmail: string | undefined,
  userProfileEmail: string | undefined,
): boolean => {
  return currentUserEmail?.toLowerCase() === userProfileEmail?.toLowerCase();
};
